import React, { useState, useEffect } from 'react';
import { getUsuarios, cambiarEstadoUsuario, getUsuarioPorDocumento, actualizarInfoLaboral, getJefes, actualizarInfoPersonal } from '../../services/Auth/userService';
import { getAreas } from '../../services/Admin/areaService';
import { getRoles } from '../../services/Admin/rolService';

import ReusableTable from '../../components/ReusableTable';
import ReusableSearchBar from '../../components/ReusableSearchBar';
import Sidebar from '../../components/Sidebar';
import SoporteLayout from '../../layouts/SoporteLayout';
import Modal from '../../components/Modal';
import { jwtDecode } from 'jwt-decode';
import withAuth from '../../routes/withAuth';

const AdministrarUsuariosPage = () => {
  const [usuarios, setUsuarios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Estados para el modal de editar información laboral
  const [showInfoLaboralModal, setShowInfoLaboralModal] = useState(false);
  const [selectedUsuario, setSelectedUsuario] = useState(null);
  const [jefes, setJefes] = useState([]);
  const [areas, setAreas] = useState([]);
  const [roles, setRoles] = useState([]);
  const [infoLaboralForm, setInfoLaboralForm] = useState({
    jefeInmediato: '',
    area: '',
    rol: ''
  });
  const [modalLoading, setModalLoading] = useState(false);
  const [modalError, setModalError] = useState('');

  // Estados para el modal de editar información personal
  const [showInfoPersonalModal, setShowInfoPersonalModal] = useState(false);
  const [selectedUsuarioPersonal, setSelectedUsuarioPersonal] = useState(null);
  const [infoPersonalForm, setInfoPersonalForm] = useState({
    nombres: '',
    apellidos: '',
    numeroDocumento: '',
    celular: '',
    email: ''
  });
  const [modalPersonalLoading, setModalPersonalLoading] = useState(false);
  const [modalPersonalError, setModalPersonalError] = useState('');

  const fetchUsuarios = async () => {
    setLoading(true);
    setError('');
    const result = await getUsuarios();
    if (result.success) {
      setUsuarios(result.data);
    } else {
      setError(result.message || 'No se pudieron cargar los usuarios.');
      setUsuarios([]);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchUsuarios();
  }, []);

  const handleCambiarEstado = async (idUsuario, estadoActual) => {
    const nuevoEstado = estadoActual ? 0 : 1;
    const result = await cambiarEstadoUsuario(idUsuario, nuevoEstado);
    if (result.success) {
      alert('Estado del usuario cambiado con éxito.');
      fetchUsuarios(); // Volver a cargar los usuarios para reflejar el cambio
    } else {
      alert(`Error al cambiar el estado: ${result.message}`);
    }
  };

  const handleSearch = async (documento) => {
    setLoading(true);
    setError('');
    const result = await getUsuarioPorDocumento(documento);
    if (result.success) {
      setUsuarios([result.data]);
    } else {
      setError(result.message);
      setUsuarios([]);
    }
    setLoading(false);
  };

  // Funciones para el modal de información laboral
  const handleAbrirInfoLaboralModal = async (usuario) => {
    console.log('Usuario seleccionado:', usuario); // Debug
    setSelectedUsuario(usuario);
    
    // Inicializar el formulario con los valores actuales del usuario
    const initialForm = {
      jefeInmediato: usuario.jefeInmediatoId ? usuario.jefeInmediatoId.toString() : '',
      area: usuario.areaId ? usuario.areaId.toString() : '',
      rol: usuario.rolId ? usuario.rolId.toString() : ''
    };
    console.log('Formulario inicial:', initialForm); // Debug
    setInfoLaboralForm(initialForm);
    
    setModalError('');
    setShowInfoLaboralModal(true);
    setModalLoading(true);
    
    try {
      // Cargar datos para los selectores
      const [jefesData, areasData, rolesData] = await Promise.all([
        getJefes(),
        getAreas(),
        getRoles()
      ]);
      
      console.log('Jefes cargados:', jefesData); // Debug
      console.log('Áreas cargadas:', areasData); // Debug
      console.log('Roles cargados:', rolesData); // Debug
      
      setJefes(jefesData || []);
      setAreas(areasData || []);
      setRoles(rolesData || []);
    } catch (error) {
      setModalError('Error al cargar los datos necesarios');
      console.error('Error cargando datos:', error);
    }
    
    setModalLoading(false);
  };

  const handleCerrarInfoLaboralModal = () => {
    setShowInfoLaboralModal(false);
    setSelectedUsuario(null);
    setInfoLaboralForm({
      jefeInmediato: '',
      area: '',
      rol: ''
    });
    setModalError('');
  };

  const handleInfoLaboralChange = (e) => {
    const { name, value } = e.target;
    console.log('Cambio en campo:', name, 'Valor:', value); // Debug
    setInfoLaboralForm(prev => {
      const newForm = { ...prev, [name]: value };
      console.log('Formulario actualizado:', newForm); // Debug
      return newForm;
    });
  };

  const handleGuardarInfoLaboral = async (e) => {
    e.preventDefault();
    
    // Validar que al menos un campo tenga valor
    if (!infoLaboralForm.jefeInmediato && !infoLaboralForm.area && !infoLaboralForm.rol) {
      setModalError('Debe seleccionar al menos un campo para actualizar');
      return;
    }

    setModalLoading(true);
    setModalError('');
    
    try {
      // Preparar el objeto con solo los campos que tienen valor válido
      const updateData = {};
      
      // Solo agregar jefeInmediatoId si tiene un valor válido
      if (infoLaboralForm.jefeInmediato && infoLaboralForm.jefeInmediato !== '' && infoLaboralForm.jefeInmediato !== 'null') {
        updateData.jefeInmediatoId = parseInt(infoLaboralForm.jefeInmediato, 10);
      }
      
      // Solo agregar areaId si tiene un valor válido
      if (infoLaboralForm.area && infoLaboralForm.area !== '' && infoLaboralForm.area !== 'null') {
        updateData.areaId = parseInt(infoLaboralForm.area, 10);
      }
      
      // Solo agregar rolId si tiene un valor válido
      if (infoLaboralForm.rol && infoLaboralForm.rol !== '' && infoLaboralForm.rol !== 'null') {
        updateData.rolId = parseInt(infoLaboralForm.rol, 10);
      }

      // Solo enviar si hay datos para actualizar
      if (Object.keys(updateData).length === 0) {
        setModalError('No hay datos válidos para actualizar');
        setModalLoading(false);
        return;
      }

      console.log('Datos del formulario:', infoLaboralForm); // Debug
      console.log('Datos a enviar:', updateData); // Debug

      const result = await actualizarInfoLaboral(selectedUsuario.idUsuario, updateData);
      
      if (result.success) {
        alert('Información laboral actualizada con éxito');
        handleCerrarInfoLaboralModal();
        fetchUsuarios(); // Recargar la lista de usuarios
      } else {
        setModalError(result.message || 'Error al actualizar la información laboral');
      }
    } catch (error) {
      setModalError('Error al procesar la solicitud');
      console.error('Error actualizando info laboral:', error);
    }
    
    setModalLoading(false);
  };

  // Funciones para el modal de información personal
  const handleAbrirInfoPersonalModal = async (usuario) => {
    console.log('Usuario seleccionado para info personal:', usuario); // Debug
    setSelectedUsuarioPersonal(usuario);
    
    // Inicializar el formulario con los valores actuales del usuario
    const initialForm = {
      nombres: usuario.nombres || '',
      apellidos: usuario.apellidos || '',
      numeroDocumento: usuario.numeroDocumento || '',
      celular: usuario.telefono || '', // Usar telefono del JSON
      email: usuario.email || ''
    };
    console.log('Formulario inicial personal:', initialForm); // Debug
    setInfoPersonalForm(initialForm);
    
    setModalPersonalError('');
    setShowInfoPersonalModal(true);
    setModalPersonalLoading(true);
    

    
    setModalPersonalLoading(false);
  };

  const handleCerrarInfoPersonalModal = () => {
    setShowInfoPersonalModal(false);
    setSelectedUsuarioPersonal(null);
    setInfoPersonalForm({
      nombres: '',
      apellidos: '',
      numeroDocumento: '',
      celular: '',
      email: ''
    });
    setModalPersonalError('');
  };

  const handleInfoPersonalChange = (e) => {
    const { name, value } = e.target;
    console.log('Cambio en campo personal:', name, 'Valor:', value); // Debug
    console.log('Tipo de dato del valor:', typeof value); // Debug
    setInfoPersonalForm(prev => {
      const newForm = { ...prev, [name]: value };
      console.log('Formulario personal actualizado:', newForm); // Debug
      return newForm;
    });
  };

  const handleGuardarInfoPersonal = async (e) => {
    e.preventDefault();
    
    // Validar que todos los campos obligatorios tengan valor
    if (!infoPersonalForm.nombres || !infoPersonalForm.apellidos || !infoPersonalForm.numeroDocumento || !infoPersonalForm.celular || !infoPersonalForm.email) {
      setModalPersonalError('Todos los campos son obligatorios');
      return;
    }

    setModalPersonalLoading(true);
    setModalPersonalError('');
    
    try {

      
      // Preparar el objeto con los datos a actualizar
      const updateData = {
        nombres: infoPersonalForm.nombres,
        apellidos: infoPersonalForm.apellidos,
        numeroDocumento: infoPersonalForm.numeroDocumento,
        celular: infoPersonalForm.celular,
        email: infoPersonalForm.email
      };

      console.log('Datos del formulario personal:', infoPersonalForm); // Debug
      console.log('Datos a enviar personal:', updateData); // Debug
      console.log('infoPersonalId a usar:', selectedUsuarioPersonal.infoPersonalId); // Debug

      // Usar el nuevo servicio para actualizar información personal
      const result = await actualizarInfoPersonal(selectedUsuarioPersonal.infoPersonalId, updateData);
      
      if (result.success) {
        alert('Información personal actualizada con éxito');
        handleCerrarInfoPersonalModal();
        fetchUsuarios(); // Recargar la lista de usuarios
      } else {
        setModalPersonalError(result.message || 'Error al actualizar la información personal');
      }
    } catch (error) {
      setModalPersonalError('Error al procesar la solicitud');
      console.error('Error actualizando info personal:', error);
    }
    
    setModalPersonalLoading(false);
  };

  const columns = [
    { key: 'idUsuario', label: 'ID' },
    { key: 'nombres', label: 'Nombres' },
    { key: 'apellidos', label: 'Apellidos' },
    { key: 'numeroDocumento', label: 'Numero de Documento' },
    { key: 'email', label: 'Email' },
    { key: 'rolNombre', label: 'Rol' },
    { key: 'areaNombre', label: 'Área' },
    { key: 'estado', label: 'Estado', formatter: (estado) => estado ? 'Activo' : 'Inactivo' },
  ];

  const actionRender = (usuario) => (
    <div style={{ display: 'flex', gap: '10px' }}>
      <button className='view-btn' onClick={() => handleCambiarEstado(usuario.idUsuario, usuario.estado)}>
        Cambiar Estado
      </button>
      <button className='edit-btn' onClick={() => handleAbrirInfoLaboralModal(usuario)}>
        Editar Info Laboral
      </button>
      <button 
        className='edit-btn' 
        onClick={() => handleAbrirInfoPersonalModal(usuario)}
        style={{
          backgroundColor: '#28a745',
          color: 'white',
          border: 'none',
          padding: '8px 16px',
          borderRadius: '4px',
          cursor: 'pointer',
          fontSize: '14px'
        }}
      >
        Edit Info Personal
      </button>
    </div>
  );

  // Detectar si el usuario es soporte técnico
  const isSoporte = (() => {
    const token = localStorage.getItem('authToken');
    if (!token) return false;
    try {
      const decoded = jwtDecode(token);
      return decoded.rolId === '3';
    } catch {
      return false;
    }
  })();

  const content = (
    <div>
      <ReusableSearchBar
        onSearch={handleSearch}
        onClear={fetchUsuarios}
        placeholder="Buscar por documento..."
        clearButtonText="Mostrar Todos"
      />
      {loading && <p>Cargando...</p>}
      {error && <p style={{ color: 'red' }}>Error: {error}</p>}
      {!loading && (
        <ReusableTable
          columns={columns}
          data={usuarios}
          actionRender={actionRender}
        />
      )}
      
      {/* Modal de Editar Información Laboral */}
      {showInfoLaboralModal && (
        <Modal
          title="Editar Información Laboral"
          onClose={handleCerrarInfoLaboralModal}
          actions={
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '20px' }}>
              <button 
                onClick={handleCerrarInfoLaboralModal}
                style={{
                  padding: '10px 20px',
                  border: '1px solid #ccc',
                  borderRadius: '5px',
                  backgroundColor: '#f8f9fa',
                  cursor: 'pointer'
                }}
              >
                Cancelar
              </button>
              <button 
                onClick={handleGuardarInfoLaboral}
                disabled={modalLoading}
                style={{
                  padding: '10px 20px',
                  border: 'none',
                  borderRadius: '5px',
                  backgroundColor: '#007bff',
                  color: 'white',
                  cursor: modalLoading ? 'not-allowed' : 'pointer',
                  opacity: modalLoading ? 0.6 : 1
                }}
              >
                {modalLoading ? 'Guardando...' : 'Guardar'}
              </button>
            </div>
          }
        >
          {modalLoading && <p>Cargando datos...</p>}
          {modalError && <p style={{ color: 'red', marginBottom: '15px' }}>{modalError}</p>}
          
          <form onSubmit={handleGuardarInfoLaboral}>
            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                Jefe Inmediato
              </label>
              <select
                name="jefeInmediato"
                value={infoLaboralForm.jefeInmediato}
                onChange={handleInfoLaboralChange}
                style={{
                  width: '100%',
                  padding: '8px',
                  border: '1px solid #ccc',
                  borderRadius: '4px'
                }}
              >
                <option value="">Seleccione un jefe...</option>
                {jefes.map(jefe => (
                  <option key={jefe.idUsuario} value={jefe.idUsuario}>
                    {jefe.nombres} {jefe.apellidos}
                  </option>
                ))}
              </select>
            </div>

            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                Área
              </label>
              <select
                name="area"
                value={infoLaboralForm.area}
                onChange={handleInfoLaboralChange}
                style={{
                  width: '100%',
                  padding: '8px',
                  border: '1px solid #ccc',
                  borderRadius: '4px'
                }}
              >
                <option value="">Seleccione un área...</option>
                {areas.map(area => (
                  <option key={area.id_area} value={area.id_area}>
                    {area.nombre}
                  </option>
                ))}
              </select>
            </div>

            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                Rol
              </label>
              <select
                name="rol"
                value={infoLaboralForm.rol}
                onChange={handleInfoLaboralChange}
                style={{
                  width: '100%',
                  padding: '8px',
                  border: '1px solid #ccc',
                  borderRadius: '4px'
                }}
              >
                <option value="">Seleccione un rol...</option>
                {roles.map(rol => (
                  <option key={rol.id_rol} value={rol.id_rol}>
                    {rol.nombre}
                  </option>
                ))}
              </select>
            </div>
                     </form>
         </Modal>
       )}

       {/* Modal de Editar Información Personal */}
       {showInfoPersonalModal && (
         <Modal
           title="Editar Información Personal"
           onClose={handleCerrarInfoPersonalModal}
           actions={
             <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '20px' }}>
               <button 
                 onClick={handleCerrarInfoPersonalModal}
                 style={{
                   padding: '10px 20px',
                   border: '1px solid #ccc',
                   borderRadius: '5px',
                   backgroundColor: '#f8f9fa',
                   cursor: 'pointer'
                 }}
               >
                 Cancelar
               </button>
               <button 
                 onClick={handleGuardarInfoPersonal}
                 disabled={modalPersonalLoading}
                 style={{
                   padding: '10px 20px',
                   border: 'none',
                   borderRadius: '5px',
                   backgroundColor: '#007bff',
                   color: 'white',
                   cursor: modalPersonalLoading ? 'not-allowed' : 'pointer',
                   opacity: modalPersonalLoading ? 0.6 : 1
                 }}
               >
                 {modalPersonalLoading ? 'Guardando...' : 'Guardar'}
               </button>
             </div>
           }
         >
           {modalPersonalLoading && <p>Cargando datos...</p>}
           {modalPersonalError && <p style={{ color: 'red', marginBottom: '15px' }}>{modalPersonalError}</p>}
           
           <form onSubmit={handleGuardarInfoPersonal}>
             <div style={{ marginBottom: '15px' }}>
               <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                 Nombres*
               </label>
               <input
                 type="text"
                 name="nombres"
                 value={infoPersonalForm.nombres}
                 onChange={handleInfoPersonalChange}
                 required
                 style={{
                   width: '100%',
                   padding: '8px',
                   border: '1px solid #ccc',
                   borderRadius: '4px'
                 }}
               />
             </div>

             <div style={{ marginBottom: '15px' }}>
               <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                 Apellidos*
               </label>
               <input
                 type="text"
                 name="apellidos"
                 value={infoPersonalForm.apellidos}
                 onChange={handleInfoPersonalChange}
                 required
                 style={{
                   width: '100%',
                   padding: '8px',
                   border: '1px solid #ccc',
                   borderRadius: '4px'
                 }}
               />
             </div>



             <div style={{ marginBottom: '15px' }}>
               <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                 Número de Documento*
               </label>
               <input
                 type="text"
                 name="numeroDocumento"
                 value={infoPersonalForm.numeroDocumento}
                 onChange={handleInfoPersonalChange}
                 required
                 style={{
                   width: '100%',
                   padding: '8px',
                   border: '1px solid #ccc',
                   borderRadius: '4px'
                 }}
               />
             </div>

             <div style={{ marginBottom: '15px' }}>
               <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                 Celular*
               </label>
               <input
                 type="text"
                 name="celular"
                 value={infoPersonalForm.celular}
                 onChange={handleInfoPersonalChange}
                 required
                 style={{
                   width: '100%',
                   padding: '8px',
                   border: '1px solid #ccc',
                   borderRadius: '4px'
                 }}
               />
             </div>

             <div style={{ marginBottom: '15px' }}>
               <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                 Email*
               </label>
               <input
                 type="email"
                 name="email"
                 value={infoPersonalForm.email}
                 onChange={handleInfoPersonalChange}
                 required
                 style={{
                   width: '100%',
                   padding: '8px',
                   border: '1px solid #ccc',
                   borderRadius: '4px'
                 }}
               />
             </div>
           </form>
         </Modal>
       )}
     </div>
   );

  if (isSoporte) {
    return <SoporteLayout>{content}</SoporteLayout>;
  }

  return (
    <div>
      {content}
    </div>
  );
};

export default withAuth(AdministrarUsuariosPage); 